import { type ApiContainerStockEntry, type ApiPlannedContainerAction, EnumContainerActionRequest } from '@Model';
import {
    type ContainerPlanSource,
    type ContainerTypeDetailsByTypeId,
    toComingCards,
} from '@Modules/Orders/Editing/Containers';
import { type LinkableField } from '@synqo/frontend-component-library';
import { type ContainerPosition, MAX_COUNT_COLLECTIVE, MIN_COUNT } from './containerPosition';

/** What the containers of a stock line hold, as the stock records it. */
export interface StockLineContent {
    id: string;
    label: string;
}

/**
 * A stock line the list can show: the projection the server sends, reduced to what the row needs
 * to decide its own shape.
 */
export interface ContainerStockLine {
    /**
     * Keys the row. A collective line names no container and is keyed on its type instead.
     *
     * Unique because {@link toStockLines} is what builds the list: the server cuts a pool into one
     * entry per standing time, and those share this key by design.
     */
    id: string;
    containerTypeId: string;
    containerTypeLabel: string;
    containerTypeStoredLabel: string;
    source: ContainerPlanSource;
    containerId: string | undefined;
    containerNumber: string;
    /** How many containers stand behind this line: one, or the pool containers added up. */
    stockCount: number;
    /** Since when it stands there; for a pool, when the last of them arrived. Unset for a planned placement. */
    standingSince: string | undefined;
    /** A line naming a container stands for that one; a nameless line counts a pool. Unset while unknown. */
    usesIndividualManagement: boolean | undefined;
    /** What the containers hold; unset while the stock records nothing, or a pool holds several. */
    content?: StockLineContent;
    /**
     * Where the containers stand, as the stock notes it; empty while it notes nothing, or a pool
     * stands in several places.
     */
    remarks: string;
}

/**
 * The lines the list shows for the stock the server sends.
 *
 * The server sends one entry per standing time. The wizard asks for a *type* — one line, one pick,
 * one count — so the entries of a pool are added up here. It is also what makes the line id an id: two
 * entries of one pool carry the same one.
 */
// @ai-generated 2026-09-16T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
export function toStockLines(
    entries: readonly ApiContainerStockEntry[],
    containerTypeOf: (containerType: LinkableField | undefined) => string,
): ContainerStockLine[] {
    const byId = new Map<string, ContainerStockLine>();

    for (const entry of entries) {
        const line = toStockLine(entry, containerTypeOf);
        const standing = byId.get(line.id);
        byId.set(line.id, standing ? addUp(standing, line) : line);
    }

    return [...byId.values()];
}

/**
 * Two entries of one line, added up: how many stand there, when the last of them arrived, and what
 * they hold and where they stand as long as they all agree.
 */
// @ai-generated 2026-09-25T12:44:51+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
function addUp(standing: ContainerStockLine, line: ContainerStockLine): ContainerStockLine {
    return {
        ...standing,
        stockCount: standing.stockCount + line.stockCount,
        standingSince: laterOf(standing.standingSince, line.standingSince),
        content: standing.content?.id === line.content?.id ? standing.content : undefined,
        remarks: standing.remarks === line.remarks ? standing.remarks : '',
    };
}

/**
 * The line a single stock entry contributes.
 *
 * The only thing left to read off an entry is which of the two kinds it is: an entry carrying a
 * container is an individually tracked one, an entry without is a counted pool.
 */
// @ai-generated 2026-09-25T12:44:51+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
export function toStockLine(
    entry: ApiContainerStockEntry,
    containerTypeOf: (containerType: LinkableField | undefined) => string,
): ContainerStockLine {
    const containerId = entry.container.foreign_key;
    const contentId = entry.content.foreign_key;

    return {
        id: containerId ?? entry.container_type.foreign_key ?? '',
        containerTypeId: entry.container_type.foreign_key ?? '',
        containerTypeLabel: containerTypeOf(entry.container_type),
        containerTypeStoredLabel: entry.container_type.display_string,
        source: { standingTimeIds: [entry.standing_time_id] },
        containerId,
        containerNumber: entry.container.display_string,
        stockCount: 1,
        standingSince: entry.standing_since,
        usesIndividualManagement: !!containerId,
        content: contentId ? { id: contentId, label: entry.content.display_string } : undefined,
        remarks: entry.remarks,
    };
}

/** One line per requisition and container or type for the placements planned at the site. */
// @ai-generated 2026-10-21T11:23:08+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
export function toPlannedLines(
    planned: ApiPlannedContainerAction[],
    containerTypeOf: (containerType: LinkableField | undefined) => string,
    management: ContainerTypeDetailsByTypeId,
): ContainerStockLine[] {
    return toComingCards(planned, NO_ORDER, [], []).map(({ key, planned: first, count }) => {
        const containerTypeId = first.container_type.foreign_key ?? '';
        const contentId = first.content.foreign_key;

        return {
            id: `planned:${key}`,
            containerTypeId,
            containerTypeLabel: containerTypeOf(first.container_type),
            containerTypeStoredLabel: first.container_type.display_string,
            source: { plannedAction: first },
            containerId: first.container.foreign_key,
            containerNumber: first.container.display_string,
            stockCount: count,
            standingSince: undefined,
            usesIndividualManagement: management.get(containerTypeId)?.usesIndividualManagement,
            content: contentId ? { id: contentId, label: first.content.display_string } : undefined,
            remarks: first.remarks,
        };
    });
}

const NO_ORDER = { id: '', number: '' };

/** The planned placement a line stands for. */
export const plannedActionOf = (line: ContainerStockLine): ApiPlannedContainerAction | undefined =>
    'plannedAction' in line.source ? line.source.plannedAction : undefined;

/** Which line a position was picked from, so the list can mark it. */
export const lineIdOf = (position: Pick<ContainerPosition, 'containerId' | 'containerTypeId'>): string =>
    position.containerId ?? position.containerTypeId;

/** The line a position was picked from: the standing one first, then the preferred planned one. */
// @ai-generated 2026-10-21T11:23:08+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
export function lineOf(
    lines: readonly ContainerStockLine[],
    position: Pick<ContainerPosition, 'containerId' | 'containerTypeId'>,
    preferredId?: string,
): ContainerStockLine | undefined {
    const id = lineIdOf(position);
    const standing = lines.find((line) => line.id === id);
    if (standing) {
        return standing;
    }

    const planned = lines.filter((line) => plannedActionOf(line) && lineIdOf(line) === id);
    return planned.find((line) => line.id === preferredId) ?? planned[0];
}

/** How many containers of a position's line stand at the site or are planned for it. */
// @ai-generated 2026-10-22T16:29:04+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
export function recordedCountOf(
    lines: readonly ContainerStockLine[],
    position: Pick<ContainerPosition, 'containerId' | 'containerTypeId'>,
): number | undefined {
    const id = lineIdOf(position);
    const matching = lines.filter((line) => lineIdOf(line) === id);
    return matching.length > 0 ? matching.reduce((sum, line) => sum + line.stockCount, 0) : undefined;
}

/** How many containers an order picked from a line asks for at first: a whole pool for a pickup or an emptying. */
// @ai-generated 2026-10-21T11:23:08+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
export function initialCountOf(line: ContainerStockLine, request: EnumContainerActionRequest): number {
    const takesThePool = request === EnumContainerActionRequest.Collect || request === EnumContainerActionRequest.Empty;
    if (!takesThePool || line.usesIndividualManagement !== false) {
        return MIN_COUNT;
    }

    return Math.min(line.stockCount, MAX_COUNT_COLLECTIVE);
}

/** The stock lines matching a search, matched on the text the row shows. */
export function filterStockLines(lines: readonly ContainerStockLine[], search: string): ContainerStockLine[] {
    const needle = search.trim().toLocaleLowerCase();
    if (!needle) {
        return [...lines];
    }

    return lines.filter((line) =>
        [line.containerTypeLabel, line.containerNumber].some((text) => text.toLocaleLowerCase().includes(needle)),
    );
}

/**
 * The order the list shows its lines in: the standing stock before the planned placements, by container type, and
 * within a type by how long the containers have stood there.
 */
export function sortStockLines(lines: readonly ContainerStockLine[]): ContainerStockLine[] {
    return [...lines].sort(
        (left, right) =>
            Number(!!plannedActionOf(left)) - Number(!!plannedActionOf(right)) ||
            left.containerTypeLabel.localeCompare(right.containerTypeLabel) ||
            (left.standingSince ?? '').localeCompare(right.standingSince ?? ''),
    );
}

/**
 * The date of the last movement the stock knows of, which the list stamps its header with.
 *
 * The newest start is the latest thing that happened to the stock; without any standing lines there
 * is nothing to date.
 */
export function lastStockMovement(lines: readonly ContainerStockLine[]): string | undefined {
    return lines.reduce<string | undefined>((latest, line) => laterOf(latest, line.standingSince), undefined);
}

const laterOf = (left: string | undefined, right: string | undefined) =>
    left === undefined || (right !== undefined && right > left) ? right : left;
